import classNames from 'classnames';
import { PrimaryButton } from '@/common/Button';
import { FinanceStatus } from '~/components/common/FinanceCard/constants';
import StatusIndicator from '~/components/common/StatusIndicator';
import { useGetShowEVTaxTreatment } from '~/hooks/vdp';
import { useButtonHandler } from '~/hooks/vdp/cta';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  CardContents,
  CardHeader,
  LineItem,
  PaymentBuyDownGrid,
  PaymentDivider,
  Price,
  PriceBreakdown,
  PriceContainer,
  SectionDivider,
  SummaryLineItem
} from './Card.styles';
import usePaymentSummary from '../../hooks/selectors/useGetPaymentSummary';
import useGetStartedText from '../../hooks/selectors/useGetStartedText';
import roundRateToPercent from '../../utils/roundRateToPercent';
import CkPromoLineItem from '../CkPromoLineItem';
import { CleanTaxSavingsCard } from '../CleanTaxSavingsCard';
import TradeInSection from '../TradeInSection';

export default function PayMonthlyCard() {
  const getStartedText = useGetStartedText();
  const {
    credits: { creditKarmaPromoAmount, tradeInCredit, totalDiscountAmount },
    dueAtOrder: { additionalCashDown },
    debits: { deliveryCharge, taxTitleReg, vehiclePrice },
    financing: { apr, monthlyPayment, term },
    totals: { amountFinanced, subTotal, totalCashDue, totalPrice }
  } = usePaymentSummary();
  const shouldShowEvTreatment = useGetShowEVTaxTreatment();
  const getStarted = useButtonHandler();
  const showSubtotal = Boolean(tradeInCredit);
  const discountLineItem = creditKarmaPromoAmount
    ? totalDiscountAmount - creditKarmaPromoAmount
    : totalDiscountAmount;

  return (
    <section data-qa="pay-monthly-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <p className="tl-header-m">Your Financing</p>
          <StatusIndicator
            status={FinanceStatus.SoftPull}
            text="Pre-Qualified"
          />
        </div>
        <p className="text-grey-5 tl-body-l">
          You’re pre-qualified to see real monthly payments on every vehicle.
        </p>
      </CardHeader>
      <CardContents>
        {shouldShowEvTreatment && (
          <CleanTaxSavingsCard className="max-w-[448px]" />
        )}
        <PaymentBuyDownGrid>
          <div className="flex flex-col m:flex-row">
            <PriceContainer>
              <Price>${formatAndDropDecimal(totalCashDue)}</Price>
              <p className="tl-body-m">Total Due at Order</p>
            </PriceContainer>
            <PaymentDivider />
            <PriceContainer>
              <Price>${formatAndDropDecimal(monthlyPayment)}</Price>
              <p className="tl-body-l">Monthly Payment</p>
              <p className="mt-8 text-grey-5 tl-body-s">
                Includes {roundRateToPercent(apr)}% APR for {term} months <br />
                and a $10/mo autopay discount
              </p>
            </PriceContainer>
            <SectionDivider />
          </div>
        </PaymentBuyDownGrid>

        <PriceBreakdown>
          <ul className="mb-12 border-b border-grey-2 pb-12 m:mb-24 m:pb-24">
            <LineItem>
              <span>Vehicle price</span>
              <span>${formatAndDropDecimal(vehiclePrice)}</span>
            </LineItem>
            <LineItem className={creditKarmaPromoAmount > 0 ? '' : 'hidden'}>
              <CkPromoLineItem />
              <span>- ${formatAndDropDecimal(creditKarmaPromoAmount)}</span>
            </LineItem>
            <LineItem className={discountLineItem > 0 ? '' : 'hidden'}>
              <span>Discount</span>
              <span>- ${formatAndDropDecimal(discountLineItem)}</span>
            </LineItem>
            <LineItem data-qa="shipping-fee-line">
              <span>{deliveryCharge ? 'Nonrefundable s' : 'S'}hipping fee</span>
              <span>${formatAndDropDecimal(deliveryCharge)}</span>
            </LineItem>
            <LineItem className="mb-0">
              <span>Tax, title, registration</span>
              <span>${formatAndDropDecimal(taxTitleReg)}</span>
            </LineItem>
          </ul>

          <ul
            className={classNames('mb-16 border-b border-grey-2 pb-12', {
              hidden: !showSubtotal
            })}
            data-qa="trade-in-details"
          >
            <SummaryLineItem>
              <span>Subtotal</span>
              <span>${formatAndDropDecimal(subTotal)}</span>
            </SummaryLineItem>
            <TradeInSection
              tradeInCredit={tradeInCredit}
              hasDeliveryCharge={Boolean(deliveryCharge)}
              card="monthly"
            />
          </ul>

          <ul>
            <SummaryLineItem>
              <span>Total Purchase Price</span>
              <span>${formatAndDropDecimal(totalPrice)}</span>
            </SummaryLineItem>
          </ul>

          <ul>
            <div className="my-24 rounded-[8px] border border-solid border-grey-3 p-16">
              <div
                className={classNames(
                  'mb-16 flex flex-col gap-8 border-b border-grey-2 pb-8 text-grey-5 tl-body-xs m:tl-body-s',
                  { hidden: !deliveryCharge && !totalCashDue }
                )}
              >
                {deliveryCharge ? (
                  <li className="flex justify-between">
                    <span>
                      {deliveryCharge ? 'Nonrefundable s' : 'S'}hipping fee
                    </span>
                    <span data-qa="shipping-fee">
                      ${formatAndDropDecimal(deliveryCharge)}
                    </span>
                  </li>
                ) : null}
                <li className="flex justify-between">
                  <span>Cash down payment</span>
                  <span data-qa="cash-down-payment">
                    ${formatAndDropDecimal(additionalCashDown)}
                  </span>
                </li>
              </div>
              <SummaryLineItem className="mb-0">
                <span>Total Due at Order</span>
                <span data-qa="total-due-at-order">
                  ${formatAndDropDecimal(totalCashDue)}
                </span>
              </SummaryLineItem>
            </div>
          </ul>

          <ul className="-mb-8">
            <SummaryLineItem className="rounded-[8px] border border-solid border-grey-3 p-16">
              <span>Total Amount Financed</span>
              <span>${formatAndDropDecimal(amountFinanced)}</span>
            </SummaryLineItem>
          </ul>
        </PriceBreakdown>

        <div
          className={
            getStartedText
              ? 'mx-auto mt-24 w-full max-w-[448px] m:max-w-[250px]'
              : 'hidden'
          }
        >
          <PrimaryButton
            className="!w-full"
            data-analytics-id="Price Details - Pay Monthly - Get Started"
            onClick={getStarted}
            size="medium"
          >
            {getStartedText}
          </PrimaryButton>
        </div>
      </CardContents>
    </section>
  );
}
